import { AlertTriangle, Check, ExternalLink, FileText, Loader2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  Badge,
  Button,
  Card,
  ErrorBanner,
  InfoBanner,
  Input,
  PageTitle,
  Spinner,
} from "@/components/ui";
import { toApiError } from "@/lib/api";
import { useFormat, useT, type Chave } from "@/lib/i18n";
import { isValidTaxId, money, taxIdMask } from "@/lib/format";
import { useBilling, useBillingActions, useBillingPlans } from "@/lib/queries";
import type {
  BillingCycle,
  BillingPlan,
  BillingSubscription,
  BillingType,
  Invoice,
} from "@/lib/types";

/** Tela de assinatura.
 *
 * Fica **fora** do `Layout` de propósito. O painel inteiro depende do
 * `/me/`, que devolve 402 quando a conta está suspensa — ou seja, quem
 * mais precisa pagar é justamente quem não conseguiria abrir a tela de
 * pagamento se ela morasse lá dentro. Aqui a única dependência é
 * `/billing/`, que continua respondendo com a conta bloqueada.
 */
export default function Subscription() {
  const t = useT();
  const { data, isLoading, isError, error } = useBilling();
  const { data: planos } = useBillingPlans();

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (isError || !data?.subscription) {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    return (
      <Pagina>
        <ErrorBanner
          message={
            status === 403
              ? t("sub.ownerOnly")
              : t("sub.loadFailed")
          }
        />
      </Pagina>
    );
  }

  const sub = data.subscription;

  return (
    <Pagina>
      <PageTitle
        eyebrow={t("sub.title")}
        title={sub.plan.name}
        subtitle={resumoDoEstado(sub, t)}
        action={<Link to="/" className="text-sm text-steel-muted underline underline-offset-4">{t("sub.backToDashboard")}</Link>}
      />

      <div className="space-y-6">
        <Estado sub={sub} />
        <Documento sub={sub} />
        <Planos sub={sub} planos={planos ?? []} tipos={data.billing_types} />
        <Faturas invoices={data.invoices} />
        <Cancelamento sub={sub} />
      </div>
    </Pagina>
  );
}

function Pagina({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto max-w-4xl p-4 lg:p-8">{children}</div>;
}

/** O resumo em uma frase. Recebe `t` porque é função pura, fora de
 *  componente — chamar hook aqui seria erro de regra do React. */
function resumoDoEstado(sub: BillingSubscription, t: (c: Chave, v?: Record<string, string | number>) => string) {
  if (sub.status === "trialing") {
    return t("sub.trialLeft", { n: sub.trial_days_left });
  }
  if (sub.status === "active") {
    return sub.canceled_at ? t("sub.canceledNote") : t("sub.active");
  }
  return sub.status_display;
}

// ------------------------------------------------------------- estado
function Estado({ sub }: { sub: BillingSubscription }) {
  const t = useT();
  const { locale } = useFormat();
  if (sub.status === "active" && !sub.canceled_at) {
    return (
      <InfoBanner>
        <span dangerouslySetInnerHTML={{ __html: t("sub.planActive", { plan: sub.plan.name }) }} />
        {sub.current_period_end && t("sub.activeUntil", { date: dia(sub.current_period_end, locale) })}.
        {/* Preço nulo = o plano não tem tabela no país desta oficina. Sem
            valor a anunciar, a frase some em vez de virar "R$ NaN". */}
        {sub.price !== null &&
          t("sub.nextCharge", {
            amount: money(sub.price, sub.currency, locale),
            cycle: t(sub.billing_cycle === "yearly" ? "sub.perYear" : "sub.perMonth"),
          })}
      </InfoBanner>
    );
  }

  if (sub.is_readonly || !sub.is_usable) {
    return (
      <Card className="border-red-900/60 bg-red-950/30 p-5">
        <div className="flex gap-3">
          <AlertTriangle className="mt-0.5 shrink-0 text-red-300" size={18} />
          <div className="text-sm text-red-100">
            <p className="font-semibold">
              {t(sub.is_usable ? "sub.bookingsBlocked" : "sub.suspended")}
            </p>
            <p className="mt-1 text-red-200/90">
              {t("sub.suspendedText")}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <InfoBanner>
      {t("sub.trialUntil")} <strong>{sub.trial_ends_at && dia(sub.trial_ends_at, locale)}</strong>.{" "}
      {t("sub.trialFirstCharge")} <strong>{dia(sub.next_due_date, locale)}</strong>{" "}
      {t("sub.trialNotInterrupted")}
    </InfoBanner>
  );
}

// ----------------------------------------------------------- documento
function Documento({ sub }: { sub: BillingSubscription }) {
  const t = useT();
  const { saveProfile } = useBillingActions();
  const perfil = sub.billing_profile;
  const [documento, setDocumento] = useState(perfil.document);
  const [erro, setErro] = useState("");

  // Tudo aqui era CPF/CNPJ cravado: a máscara, a validação e o mínimo de
  // onze dígitos do botão. Com EIN o campo ficava intransponível — ver
  // `taxIdMask` em lib/format.ts.
  const pais = sub.country;
  const digitos = documento.replace(/\D/g, "");
  const valido = isValidTaxId(documento, pais, perfil.document_required);

  async function salvar() {
    setErro("");
    if (!valido) {
      setErro(t("sub.taxIdInvalid", { documento: perfil.document_label }));
      return;
    }
    try {
      await saveProfile.mutateAsync({ document: digitos });
    } catch (e) {
      setErro(toApiError(e).message);
    }
  }

  return (
    <Card className="p-5">
      <h2 className="font-display text-lg font-bold">{t("sub.billingData")}</h2>
      <p className="mt-1 text-sm text-steel-muted">
        {t(perfil.document_required ? "sub.taxIdHint" : "sub.taxIdOptional", {
          documento: perfil.document_label,
        })}
      </p>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <Input
            label={perfil.document_label}
            value={documento}
            onChange={(e) => setDocumento(taxIdMask(e.target.value, pais))}
            placeholder={perfil.document_example}
            inputMode="numeric"
            error={erro}
          />
        </div>
        <Button onClick={salvar} disabled={saveProfile.isPending || !valido}>
          {saveProfile.isPending ? <Loader2 className="size-4 animate-spin" /> : t("common.save")}
        </Button>
      </div>

      {perfil.document && perfil.document_ok && !erro && (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-emerald-400">
          <Check size={14} /> {t("sub.documentOk")}
        </p>
      )}
    </Card>
  );
}

// -------------------------------------------------------------- planos
const NOME_DO_PAGAMENTO: Record<BillingType, Chave> = {
  card: "sub.creditCard",
};

function Planos({
  sub,
  planos,
  tipos,
}: {
  sub: BillingSubscription;
  planos: BillingPlan[];
  tipos: BillingType[];
}) {
  const t = useT();
  const { locale } = useFormat();
  const { checkout } = useBillingActions();
  const [ciclo, setCiclo] = useState<BillingCycle>(sub.billing_cycle);
  const [forma, setForma] = useState<BillingType>(
    (tipos.includes(sub.payment_method as BillingType)
      ? sub.payment_method
      : (tipos[0] ?? "card")) as BillingType,
  );
  const [erro, setErro] = useState("");

  async function assinar(code: string) {
    setErro("");
    try {
      const r = await checkout.mutateAsync({ plan: code, cycle: ciclo, billing_type: forma });
      // O pagamento acontece no Checkout do Stripe. Nenhum dado de cartão
      // passa pelo nosso servidor.
      //
      // Vem vazio quando foi troca de plano: aí não há o que pagar agora,
      // o ajuste entra por pró-rata na próxima fatura e a tela só
      // recarrega com o plano novo.
      if (r.payment_url) window.location.href = r.payment_url;
    } catch (e) {
      setErro(toApiError(e).message);
    }
  }

  return (
    <Card className="p-5">
      <h2 className="font-display text-lg font-bold">{t("sub.choosePlan")}</h2>
      <p className="mt-1 text-sm text-steel-muted">
        {t("sub.noLockIn")}
      </p>

      <div className="mt-4 flex flex-wrap gap-4">
        <Alternador
          label={t("sub.billing")}
          valor={ciclo}
          opcoes={[
            { valor: "monthly", rotulo: t("sub.monthly") },
            { valor: "yearly", rotulo: t("sub.yearly") },
          ]}
          onChange={(v) => setCiclo(v as BillingCycle)}
        />
        {/* Com uma forma só, o seletor vira uma pergunta sem escolha —
            ruído numa tela que precisa levar ao botão de assinar. Ele
            volta sozinho quando a API devolver mais de uma. */}
        {tipos.length > 1 && (
          <Alternador
            label={t("sub.paymentMethod")}
            valor={forma}
            opcoes={tipos.map((tipo) => ({ valor: tipo, rotulo: t(NOME_DO_PAGAMENTO[tipo]) ?? tipo }))}
            onChange={(v) => setForma(v as BillingType)}
          />
        )}
      </div>

      {erro && (
        <div className="mt-4">
          <ErrorBanner message={erro} />
        </div>
      )}

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {planos.map((p) => {
          const atual = p.code === sub.plan.code;
          const preco = ciclo === "yearly" ? p.price_yearly : p.price_monthly;
          return (
            <div
              key={p.code}
              className={`rounded-[4px] border p-4 ${atual ? "border-racing-600/60 bg-racing-500/5" : "border-line"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-display text-base font-bold">{p.name}</h3>
                {atual && (
                  <Badge className="border-racing-600/40 bg-racing-500/10 text-racing-400">
                    {t("sub.currentPlan")}
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-2xl font-bold">
                {money(preco, p.currency, locale)}
                <span className="text-sm font-normal text-steel-dim">
                  /{t(ciclo === "yearly" ? "sub.perYear" : "sub.perMonth")}
                </span>
              </p>
              {ciclo === "yearly" && Number(p.yearly_savings) > 0 && (
                <p className="text-xs text-emerald-400">
                  {t("sub.yearlySavings", { amount: money(p.yearly_savings, p.currency, locale) })}
                </p>
              )}
              <p className="mt-2 text-sm text-steel-muted">
                {t("sub.upToPros", { n: p.max_professionals })}
                {p.description && ` ${p.description}`}
              </p>
              <Button
                className="mt-4 w-full"
                variant={atual ? "secondary" : "racing"}
                disabled={!sub.can_checkout || checkout.isPending}
                onClick={() => assinar(p.code)}
              >
                {checkout.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : atual && sub.status === "active" ? (
                  t("sub.updateBilling")
                ) : (
                  t("sub.subscribe")
                )}
              </Button>
            </div>
          );
        })}
      </div>

      {!sub.can_checkout && (
        <p className="mt-4 text-sm text-amber-300">
          {t("sub.needTaxId", { documento: sub.billing_profile.document_label })}
        </p>
      )}
    </Card>
  );
}

function Alternador({
  label,
  valor,
  opcoes,
  onChange,
}: {
  label: string;
  valor: string;
  opcoes: { valor: string; rotulo: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <p className="eyebrow mb-1.5 text-steel-dim">{label}</p>
      <div className="inline-flex rounded-[4px] border border-line p-0.5">
        {opcoes.map((o) => (
          <button
            key={o.valor}
            type="button"
            onClick={() => onChange(o.valor)}
            className={`rounded-[3px] px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.1em] transition ${
              valor === o.valor ? "bg-nitro-500 text-white" : "text-steel-muted hover:bg-carbon-700"
            }`}
          >
            {o.rotulo}
          </button>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------- faturas
function Faturas({ invoices }: { invoices: Invoice[] }) {
  const t = useT();
  const { locale } = useFormat();
  if (invoices.length === 0) return null;

  return (
    <Card className="p-5">
      <h2 className="font-display text-lg font-bold">{t("sub.invoices")}</h2>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-steel-dim">
            <tr>
              <th className="py-2 font-medium">{t("sub.dueDate")}</th>
              <th className="py-2 font-medium">{t("sub.amount")}</th>
              <th className="py-2 font-medium">{t("sub.status")}</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {invoices.map((f) => (
              <tr key={f.id} className="border-t border-line">
                <td className="py-2.5">{f.due_date ? dia(f.due_date, locale) : "—"}</td>
                <td className="py-2.5">{money(f.value, f.currency, locale)}</td>
                <td className="py-2.5">
                  <Badge
                    className={
                      f.is_paid
                        ? "border-emerald-800/50 bg-emerald-950/50 text-emerald-300"
                        : "border-amber-700/50 bg-amber-950/50 text-amber-300"
                    }
                  >
                    {f.status_display}
                  </Badge>
                </td>
                <td className="py-2.5 text-right">
                  {f.payment_url && (
                    <a
                      className="inline-flex items-center gap-1 text-racing-400 underline underline-offset-4"
                      href={f.payment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("sub.pay")} <ExternalLink size={13} />
                    </a>
                  )}
                  {f.is_paid && f.receipt_url && (
                    <a
                      className="inline-flex items-center gap-1 text-steel-muted underline underline-offset-4"
                      href={f.receipt_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("sub.receipt")} <FileText size={13} />
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// -------------------------------------------------------- cancelamento
function Cancelamento({ sub }: { sub: BillingSubscription }) {
  const t = useT();
  const { locale } = useFormat();
  const { cancel } = useBillingActions();
  const [aberto, setAberto] = useState(false);
  const [motivo, setMotivo] = useState("");

  if (sub.status === "canceled") return null;

  if (sub.canceled_at) {
    return (
      <p className="text-sm text-steel-dim">
        {t("sub.canceledOn", { date: dia(sub.canceled_at, locale) })}
        {sub.current_period_end && t("sub.activeUntil", { date: dia(sub.current_period_end, locale) })}
        {t("sub.resubscribe")}
      </p>
    );
  }

  return (
    <div className="text-sm text-steel-dim">
      {!aberto ? (
        <button className="underline underline-offset-4" onClick={() => setAberto(true)}>
          {t("sub.cancel")}
        </button>
      ) : (
        <Card className="p-5">
          <p className="text-steel">
            {t("sub.cancelText")}
          </p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div className="min-w-[240px] flex-1">
              <Input
                label={t("sub.whyLeaving")}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder={t("sub.whyLeavingPlaceholder")}
              />
            </div>
            <Button variant="secondary" onClick={() => setAberto(false)}>
              {t("common.back")}
            </Button>
            <Button
              variant="danger"
              disabled={cancel.isPending}
              onClick={() => cancel.mutateAsync(motivo).then(() => setAberto(false))}
            >
              {cancel.isPending ? <Loader2 className="size-4 animate-spin" /> : t("common.confirm")}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

/** A data de cobrança no formato de quem lê.
 *
 *  Era "pt-BR" cravado. 03/04 é 3 de abril aqui e 4 de março lá, e a
 *  data errada aqui é a data em que o dono acha que vai ser cobrado. */
const dia = (iso: string, locale: string) => new Date(iso).toLocaleDateString(locale);
